import {
  Badge,
  Box,
  Button,
  Input as ChakraInput,
  Divider,
  Flex,
  Heading,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  SimpleGrid,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  useDisclosure,
  useToast,
  VStack,
} from '@chakra-ui/react'
import { yupResolver } from '@hookform/resolvers/yup'
import { useEffect, useMemo, useState } from 'react'
import { FieldError, SubmitHandler, useForm } from 'react-hook-form'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import * as yup from 'yup'

import { Input } from '../../components/Form/Input'
import { Select } from '../../components/Form/Select'
import { useAuth } from '../../context/AuthContext'
import { Configuration } from '../../Interfaces/configuration'
import { api } from '../../services/api'
import { mapPrinterStatusLabel, resolvePrinterStatuses } from '../../utils/printerStatus'
import AccessControlTab from './AccessControlTab'

// Importar versão do package.json
import packageJson from '../../../package.json'

const createConfigurationsSchema = yup.object().shape({
  max_order_days: yup
    .number()
    .transform((value) => (Number.isNaN(value) ? null : value))
    .nullable(),
  oven_burn_value: yup
    .number()
    .transform((value) => (Number.isNaN(value) ? null : value))
    .nullable(),
  oven_parts: yup
    .number()
    .transform((value) => (Number.isNaN(value) ? null : value))
    .nullable(),
  work_hour_value: yup
    .number()
    .transform((value) => (Number.isNaN(value) ? null : value))
    .nullable(),
  printer_name: yup.string().nullable(),
  printer_port: yup.string().nullable(),
})

export default function ConfigurationForm() {
  const queryClient = useQueryClient()
  const toast = useToast()
  const resetDisclosure = useDisclosure()
  const [password, setPassword] = useState('')
  const [isResetting, setIsResetting] = useState(false)
  const { user } = useAuth()

  const createConfiguration = useMutation(
    async (config: Configuration) => {
      const response = await api.post('configurations', {
        max_order_days: config.max_order_days,
        oven_burn_value: config.oven_burn_value,
        oven_parts: config.oven_parts,
        work_hour_value: config.work_hour_value,
        printer_name: config.printer_name,
        printer_port: config.printer_port ?? undefined,
      })

      if (response) toast({ description: 'Cadastrado com sucesso.' })
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('configurations')
      },
      onError: ({ response }) => {
        const toastOptions = response.data.errors.map((err) => ({ description: err.message, status: 'error' }))
        toastOptions.forEach((t) => {
          toast(t)
        })
      },
    },
  )

  const updateConfiguration = useMutation(
    async (config: Configuration) => {
      const response = await api.put('configurations/' + config.id, {
        max_order_days: config.max_order_days,
        oven_burn_value: config.oven_burn_value,
        oven_parts: config.oven_parts,
        work_hour_value: config.work_hour_value,
        printer_name: config.printer_name,
        printer_port: config.printer_port ?? undefined,
      })

      if (response) toast({ description: 'Cadastrado com sucesso.' })
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('configurations')
      },
      onError: ({ response }) => {
        const toastOptions = response.data.errors.map((err) => ({ description: err.message, status: 'error' }))
        toastOptions.forEach((t) => {
          toast(t)
        })
      },
    },
  )

  const { register, handleSubmit, formState, reset, watch, setValue } = useForm({
    resolver: yupResolver(createConfigurationsSchema),
  })

  const {
    data: configData,
    refetch: refetchConfiguration,
    isFetching: isFetchingConfiguration,
  } = useQuery(
    'configurations',
    async () => {
      const { data } = await api.get('configurations/last-update')
      return data
    },
    {
      staleTime: 5000,
      refetchInterval: 10_000,
      onError: (error) => {
        console.error('Erro ao carregar configurações:', error)
      },
    },
  )

  const {
    data: printersData,
    refetch: refetchPrinters,
    isFetching: isFetchingPrinters,
  } = useQuery(
    'impressoras-list',
    async () => {
      const { data } = await api.get<{
        success: boolean
        printers: { name: string; port?: string }[]
        total: number
      }>('impressoras/list')
      return data
    },
    {
      staleTime: 30_000,
      retry: 1,
      onError: () => {},
    },
  )
  const printersList = printersData?.printers ?? []

  useEffect(() => {
    if (!configData) return

    const config = configData

    if (config?.id) {
      const formData = {
        id: config.id,
        max_order_days: config.max_order_days || 0,
        oven_burn_value: config.oven_burn_value || 0,
        oven_parts: config.oven_parts || 0,
        work_hour_value: config.work_hour_value || 0,
        printer_name: config.printer_name || '',
        printer_port: config.printer_port ?? '',
      }

      setTimeout(() => {
        reset(formData)
      }, 100)
    } else {
      reset({
        max_order_days: 0,
        oven_burn_value: 0,
        oven_parts: 0,
        work_hour_value: 0,
        printer_name: '',
        printer_port: '',
      })
    }
  }, [configData, reset])

  const { printerName, printerSocketStatus, printerDeviceStatus } = resolvePrinterStatuses(configData)
  const socketStatusLabel = mapPrinterStatusLabel(printerSocketStatus.status)
  const deviceStatusLabel = mapPrinterStatusLabel(printerDeviceStatus.status)
  const errors = formState.errors
  const currentPrinterName = watch('printer_name')

  const printerOptions = useMemo(() => {
    const base = [{ value: '', label: 'Nenhuma' }]
    const fromApi = printersList.map((impressora) => ({
      value: impressora.name,
      label: impressora.port ? `${impressora.name} (${impressora.port})` : impressora.name,
    }))
    const namesFromApi = new Set(fromApi.map((o) => o.value))
    const extra =
      currentPrinterName && !namesFromApi.has(currentPrinterName)
        ? [{ value: currentPrinterName, label: `${currentPrinterName} (configurada)` }]
        : []
    return [...base, ...fromApi, ...extra]
  }, [printersList, currentPrinterName])

  useEffect(() => {
    if (!currentPrinterName || printersList.length === 0) return
    const found = printersList.find((p) => p.name === currentPrinterName)
    if (found?.port) setValue('printer_port', found.port)
  }, [currentPrinterName, printersList, setValue])

  const handleSubmitConfiguration: SubmitHandler<Configuration> = async (values) => {
    const port =
      printersList.find((p: { name: string; port?: string }) => p.name === values.printer_name)?.port ??
      (values as Configuration).printer_port ??
      undefined
    const payload = { ...values, printer_port: port || undefined }
    if (configData?.id) {
      await updateConfiguration.mutateAsync(payload)
    } else {
      await createConfiguration.mutateAsync(payload)
    }
  }

  return (
    <Tabs colorScheme="pink" variant="enclosed">
      <TabList>
        <Tab>Parâmetros do Sistema</Tab>
        <Tab>Controle de Acesso</Tab>
      </TabList>

      <TabPanels>
        <TabPanel px={0}>
          <Box
            flex="1"
            borderRadius="8"
            bg="gray.800"
            p="8"
            as="form"
            onSubmit={handleSubmit(handleSubmitConfiguration)}>
            <Heading size="lg" fontWeight="normal">
              Editar Configurações
            </Heading>

            <Divider my="6" borderColor="gray.700" />

            <Box mb="8" p="4" bg="gray.700" borderRadius="md">
              <HStack spacing="4">
                <Text fontSize="sm" color="gray.300">
                  Versão do Sistema:
                </Text>
                <Badge colorScheme="blue" fontSize="sm">
                  v{packageJson.version}
                </Badge>
              </HStack>
            </Box>

            <VStack spacing="8" align="stretch">
              <SimpleGrid minChildWidth="240px" spacing="8" width="100%">
                <Input
                  label="Valor da hora de trabalho"
                  type="number"
                  {...register('work_hour_value')}
                  error={errors.work_hour_value as FieldError}
                />
                <Input
                  label="Valor de queima de estufa"
                  type="number"
                  {...register('oven_burn_value')}
                  error={errors.oven_burn_value as FieldError}
                />
              </SimpleGrid>
              <SimpleGrid minChildWidth="240px" spacing="8" width="100%">
                <Input
                  label="Qtd máxima de pedidos"
                  type="number"
                  {...register('max_order_days')}
                  error={errors.max_order_days as FieldError}
                />
                <Input
                  label="Quantidade média de peças por estufa"
                  type="number"
                  {...register('oven_parts')}
                  error={errors.oven_parts as FieldError}
                />
                <Select
                  label="Impressora"
                  options={printerOptions}
                  {...register('printer_name')}
                  error={errors.printer_name as FieldError}
                  placeholder="Selecione a impressora"
                />
                <Input
                  label="Porta para impressão EPL (opcional)"
                  placeholder="Ex.: USB001, IP_192.168.1.100"
                  {...register('printer_port')}
                  error={errors.printer_port as FieldError}
                />
              </SimpleGrid>
              {printersList.length === 0 && (
                <Box p="4" bg="gray.700" borderRadius="md">
                  <Text fontSize="sm" color="gray.300" mb="2">
                    Nenhuma impressora listada. Abra a aplicação &quot;Impressora API&quot; no
                    computador onde a impressora está conectada e clique em Atualizar lista.
                  </Text>
                  <Button
                    size="sm"
                    colorScheme="gray"
                    onClick={() => refetchPrinters()}
                    isLoading={isFetchingPrinters}
                  >
                    Atualizar lista de impressoras
                  </Button>
                </Box>
              )}
            </VStack>

            <Flex mt="8" justify="flex-end">
              <HStack spacing="4">
                <Button type="submit" colorScheme="pink" isLoading={formState.isSubmitting}>
                  Salvar
                </Button>
              </HStack>
            </Flex>

            <Divider my="6" borderColor="gray.700" />

            <Box p="4" bg="gray.700" borderRadius="md">
              <Heading size="sm" mb="3" color="gray.100">
                Status da Impressão
              </Heading>

              <SimpleGrid minChildWidth="280px" spacing="4" mb="4">
                <Box p="3" bg="gray.600" borderRadius="md">
                  <Text fontSize="sm" color="gray.200" fontWeight="bold" mb="1">
                    Conexão com programa da impressora
                  </Text>
                  <Text fontSize="sm" color="gray.300">
                    Status: <strong>{socketStatusLabel}</strong>
                  </Text>
                  <Text fontSize="sm" color="gray.300">
                    Clientes conectados: <strong>{printerSocketStatus.connectedClients}</strong>
                  </Text>
                  <Text fontSize="xs" color="gray.400">
                    {printerSocketStatus.message || 'Sem detalhes da conexão.'}
                  </Text>
                  <Text fontSize="xs" color="gray.500" mt="1">
                    Apenas a aplicação &quot;Impressora API&quot; (no PC da impressora) é contada. Testes com cliente WebSocket genérico não aparecem aqui.
                  </Text>
                </Box>

                <Box p="3" bg="gray.600" borderRadius="md">
                  <Text fontSize="sm" color="gray.200" fontWeight="bold" mb="1">
                    Impressora no computador cliente
                  </Text>
                  <Text fontSize="sm" color="gray.300">
                    Nome: <strong>{printerName || 'Não informado'}</strong>
                  </Text>
                  <Text fontSize="sm" color="gray.300">
                    Status: <strong>{deviceStatusLabel}</strong>
                  </Text>
                  <Text fontSize="xs" color="gray.400">
                    {printerDeviceStatus.message || 'Sem detalhes da impressora.'}
                  </Text>
                </Box>
              </SimpleGrid>

              <Button
                size="sm"
                colorScheme="gray"
                onClick={() => refetchConfiguration()}
                isLoading={isFetchingConfiguration}
              >
                Atualizar status
              </Button>
            </Box>

            <Divider my="6" borderColor="gray.700" />

            <Box p="4" bg="gray.700" borderRadius="md">
              <Heading size="sm" mb="3" color="red.300">
                Zona de Risco
              </Heading>
              <Text fontSize="sm" color="gray.300" mb="3">
                Esta ação irá zerar o estoque de todas as matérias-primas ativas.
              </Text>
              <Button colorScheme="red" onClick={resetDisclosure.onOpen}>
                Zerar estoque de matérias-primas
              </Button>
            </Box>
          </Box>
        </TabPanel>

        <TabPanel px={0}>
          <AccessControlTab />
        </TabPanel>
      </TabPanels>

      <Modal
        isOpen={resetDisclosure.isOpen}
        onClose={() => {
          if (!isResetting) resetDisclosure.onClose()
        }}
        size="lg">
        <ModalOverlay />
        <ModalContent bg="#181b23" color="white">
          <ModalHeader>Zerar estoque de matérias-primas</ModalHeader>
          <ModalCloseButton disabled={isResetting} />
          <ModalBody>
            <VStack align="stretch" spacing={4}>
              <Text>Confirme para zerar o estoque de todas as matérias-primas. Esta operação:</Text>
              <Box as="ul" pl={4} color="gray.300">
                <Box as="li">Define a quantidade de cada matéria-prima para 0.</Box>
                <Box as="li">Registra uma transação de ajuste no histórico de estoque.</Box>
                <Box as="li">Requer sua senha para confirmação.</Box>
              </Box>

              <Box>
                <Text mb={2}>Digite sua senha para confirmar:</Text>
                <ChakraInput
                  type="password"
                  placeholder="Senha do usuário logado"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  bg="gray.700"
                  borderColor="gray.600"
                  _placeholder={{ color: 'gray.400' }}
                />
              </Box>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={resetDisclosure.onClose} disabled={isResetting}>
              Cancelar
            </Button>
            <Button
              colorScheme="red"
              isLoading={isResetting}
              onClick={async () => {
                if (!user?.username) {
                  toast({ description: 'Usuário não identificado.', status: 'error' })
                  return
                }
                if (!password) {
                  toast({ description: 'Informe sua senha.', status: 'warning' })
                  return
                }
                setIsResetting(true)
                try {
                  // Verifica senha do usuário logado
                  await api.post('/login', { username: user.username, password }, { headers: { 'X-Ignore-401': true } })

                  // Chama endpoint único para zerar todo o estoque
                  await api.post('stock-transactions/reset-raw-materials')

                  toast({ description: 'Zeragem de estoque concluída.', status: 'success' })
                  setPassword('')
                  resetDisclosure.onClose()
                  try {
                    queryClient.invalidateQueries('raw_material')
                  } catch {}
                } catch (error: any) {
                  const description = error?.response?.data?.error || 'Senha incorreta ou erro ao zerar estoque.'
                  toast({ description, status: 'error' })
                } finally {
                  setIsResetting(false)
                }
              }}>
              Confirmar zeragem
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Tabs>
  )
}
